import React, { useEffect, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { notBlank } from "../../../util/ext";
import { isAdminView, formatSubscriptionStatus } from "../util";
import { useMediaTable } from "../../common/use-mediaTable";
import {
  useModuleMedia,
  useModuleDelete,
  useModuleUpdate,
} from "../moduleServices";
import { useResolvedPath, Link as RouterLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCurrentOrgId, useIsAdmin } from "../../../auth/authUtils";
import { faKey } from "@fortawesome/pro-regular-svg-icons";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableSortLabel from "@mui/material/TableSortLabel";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ShowcaseIcon from "@mui/icons-material/Star";

import PublishStatusSwitch from "../edit/PublishStatusSwitch";
import ModuleCard from "./ModuleCard";
import OwnersList from "./OwnersList";
import StatusTableCell from "../../common/StatusTableCell";
import { useCreateActiveModule } from "../../../manage-organizations/organizationServices";
import ConfirmButton from "../../../components/ConfirmButton";

const useStyles = isAdmin =>
  makeStyles(theme => ({
    root: {
      width: "calc(100% + 12px)",
      marginLeft: "-6px",
      borderRadius: "20px",
    },

    titleCell: {
      paddingLeft: 30,
    },

    orgCell: {
      maxWidth: 200,
      width: 200,
      paddingLeft: 25,
    },

    subCell: {
      maxWidth: 175,
      width: 175,
    },

    dateCell: {
      maxWidth: 140,
      width: 140,
    },

    publishCell: {
      maxWidth: 125,
      width: 125,
    },

    visuallyHidden: {
      border: 0,
      clip: "rect(0 0 0 0)",
      height: 1,
      margin: -1,
      overflow: "hidden",
      padding: 0,
      position: "absolute",
      top: 20,
      width: 1,
    },

    headerCell: {
      backgroundColor: "white",
    },

    pagination: {
      color: theme.palette.primary.darker,
      backgroundColor: "white",
      borderRadius: "20px",
    },
  }));

const ModuleItemCount = props => {
  const { id } = props;

  const moduleMedia = useModuleMedia(id);

  return moduleMedia.isLoading ? (
    <Skeleton />
  ) : (
    <div>{moduleMedia.data.length}</div>
  );
};

const ModuleTable = props => {
  const url = useResolvedPath(".").pathname;
  const currentOrgId = useCurrentOrgId();

  const {
    moduleList,
    page,
    onPageChange,
    total = 0,
    onSelectChange,
    orderBy = "name",
    order = "asc",
    onSortChange,
    onScrollVisible,
    view,
    onShowcaseClick,
    searchState,
  } = props;

  const isAdmin = isAdminView(view);
  const isCurrentlyAdmin = useIsAdmin();
  const classes = useStyles(isAdmin)();

  const { handleRequestSort, handleChangePage } = useMediaTable(
    {
      onSortChange,
      onPageChange,
      onSelectChange,
    },
    {
      sort: {
        property: orderBy,
        order,
      },
      page,
      selected: [],
      editId: null,
    },
  );

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible, tableContainerEl]);

  const createActiveModule = useCreateActiveModule();
  const updateModule = useModuleUpdate();
  const handleUndo = previousModule => async () => {
    try {
      await createActiveModule.mutateAsync({
        resourceIds: {
          moduleId: previousModule.id,
          orgId: previousModule.orgId,
        },
      });

      await updateModule.mutateAsync({
        resourceIds: { id: previousModule.id },
        data: { status: previousModule.status },
      });
    } catch (err) {
      console.log(err);
    }
  };

  const deleteModule = useModuleDelete();
  const handleClickDelete = async module => {
    await deleteModule.mutateAsync({ module, orgId: currentOrgId });
  };

  return (
    <Paper elevation={0} className={classes.root}>
      <TableContainer ref={tableContainerEl}>
        <Table stickyHeader aria-label="Modules">
          <TableHead>
            <TableRow>
              <TableCell key="module" className={classes.titleCell}>
                <TableSortLabel
                  active={orderBy === "name"}
                  direction={order}
                  onClick={() => handleRequestSort("name")}
                >
                  Title
                  {orderBy === "name" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              {isAdminView(view) && (
                <TableCell key="organization" className={classes.orgCell}>
                  Library/Organization
                </TableCell>
              )}

              <TableCell
                align="right"
                key="date-created"
                className={classes.dateCell}
              >
                <TableSortLabel
                  active={orderBy === "auditAdded"}
                  direction={order}
                  onClick={() => handleRequestSort("auditAdded")}
                >
                  Date Created
                  {orderBy === "auditAdded" ? (
                    <span className={classes.visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </span>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              {isAdminView(view) && (
                <TableCell
                  align="left"
                  key="subscription-status"
                  className={classes.subCell}
                >
                  Subscription Status
                </TableCell>
              )}

              {!isCurrentlyAdmin && (
                <TableCell key="publishStatus" className={classes.publishCell}>
                  Publish Status
                </TableCell>
              )}

              {isAdminView(view) && (
                <TableCell key="module-status">Status</TableCell>
              )}

              <TableCell key="itemCount" align="right">
                # of Items
              </TableCell>

              <TableCell
                key="module-id"
                align="center"
                style={{ width: 100 }}
              />
            </TableRow>
          </TableHead>

          <TableBody>
            {moduleList.map(module => (
              <TableRow key={module.id} hover tabIndex={-1}>
                <TableCell
                  key={"title:" + module.id}
                  style={{ paddingLeft: "30px" }}
                >
                  <ModuleCard
                    module={module}
                    view={view}
                    LinkProps={{
                      to: {
                        pathname: `${url}/${module.id}`,
                        state: { from: "/modules" },
                      },
                    }}
                  />
                </TableCell>

                {isAdminView(view) && (
                  <TableCell key={"organization:" + module.id}>
                    <OwnersList media={module} searchState={searchState} />
                  </TableCell>
                )}

                <TableCell align="right" key={"date-created:" + module.id}>
                  {new Date(module.auditAdded).toLocaleDateString()}
                </TableCell>

                {isAdminView(view) && (
                  <TableCell
                    align="left"
                    key={"subscription-status:" + module.id}
                  >
                    {formatSubscriptionStatus(module.subscribable)}
                  </TableCell>
                )}

                {!isCurrentlyAdmin && (
                  <TableCell key={"publish-status:" + module.id}>
                    <PublishStatusSwitch view="table" module={module} />
                  </TableCell>
                )}

                {isAdminView(view) && (
                  <StatusTableCell
                    key={"module-status: " + module.id}
                    status={module.status}
                  />
                )}

                <TableCell align="right" key={"itemCount:" + module.id}>
                  <ModuleItemCount id={module.id} />
                </TableCell>

                <TableCell key={"actions:" + module.id} align="center">
                  {isAdminView(view) && (
                    <Tooltip title="Showcase">
                      <IconButton
                        aria-label="showcase"
                        onClick={() => onShowcaseClick(module.id)}
                        size="large"
                      >
                        <ShowcaseIcon />
                      </IconButton>
                    </Tooltip>
                  )}

                  {!isCurrentlyAdmin && (
                    <ConfirmButton
                      data={{ ...module, orgId: currentOrgId }}
                      handleDelete={() => handleClickDelete(module)}
                      handleUndo={handleUndo}
                      dialogTitle="Delete Module?"
                      dialogBody="Are you sure you want to delete this module?"
                    />
                  )}

                  {isCurrentlyAdmin && (
                    <Tooltip title="Manage Access">
                      <IconButton
                        component={RouterLink}
                        to={{
                          pathname: `/creator/manage-access/module/${module.id}`,
                        }}
                        state={{
                          label: "Back to All Modules",
                          previousLocation: "/creator/modules",
                        }}
                        size="large"
                      >
                        <FontAwesomeIcon icon={faKey} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        classes={{
          root: classes.pagination,
        }}
        rowsPerPageOptions={[10]}
        component="div"
        count={total}
        rowsPerPage={10}
        page={page}
        onPageChange={(event, page) => handleChangePage(page)}
      />
    </Paper>
  );
};

export default ModuleTable;
